import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
} from "react";
import { useBeforeunload } from "react-beforeunload";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  IconButton,
  Button,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MenuIcon from "@mui/icons-material/Menu";
import AddIcon from "@mui/icons-material/Add";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

import {
  TransformWrapper,
  TransformComponent,
  ReactZoomPanPinchRef,
} from "react-zoom-pan-pinch";
import { toast } from "react-toastify";

import ConfirmDialog from "../../components/confirmation/ConfirmDialog";
import ActivityBar from "../../components/maps/ActivityBar";
// If you use that color menu approach:
import ColorMenu from "./ColorMenu";

import { AuthContext } from "../../context/authContext";

import { BOUNDARY_ANGLES, anglePairs, pinSvg } from "./helpers";

import Compass from "../Compass/Compass";

import { DroppedItem } from "./types"; // or wherever you define it
import {
  getIntersectionPointXY,
  intersectionPointsFromAnglePairsXY,
  lineSegmentIntersection2D,
} from "../../utils/helpers/getIntersectionPoint";
import { getDirectionsForItemXY } from "../../utils/helpers/getDirectionByAngle";
import { getColorHexCode } from "../../utils/helpers/getColorHexCode";
import SitePlanDroppedItemsOverlay from "./SitePlanDroppedItems";
import { Corner } from "./DroppedItemsOverlay";
import { useGetAllDirections } from "../../hooks/query/useDirection";
import {
  useGetSitePlan,
  useResetSitePlan,
  useUploadSitePlan,
} from "../../hooks/query/useUploadSitePlan";
import { useGetAllColors } from "../../hooks/query/useColor";
import {
  useClearMapState,
  useCreateMapState,
  useGetMapState,
} from "../../hooks/query/useVastu";

interface Pin {
  x: number;
  y: number;
  isHovered?: boolean;
  color?: string;
}

function calculateCentroid(xyPoints: { x: number; y: number }[]): {
  x: number;
  y: number;
} {
  if (xyPoints.length < 3) {
    // fallback if < 3
    return xyPoints[0] || { x: 0, y: 0 };
  }
  let centroidX = 0;
  let centroidY = 0;
  let signedArea = 0;
  for (let i = 0; i < xyPoints.length; i++) {
    const x0 = xyPoints[i].x;
    const y0 = xyPoints[i].y;
    const x1 = xyPoints[(i + 1) % xyPoints.length].x;
    const y1 = xyPoints[(i + 1) % xyPoints.length].y;
    const a = x0 * y1 - x1 * y0;
    signedArea += a;
    centroidX += (x0 + x1) * a;
    centroidY += (y0 + y1) * a;
  }
  signedArea *= 0.5;
  centroidX /= 6.0 * signedArea;
  centroidY /= 6.0 * signedArea;
  return { x: centroidX, y: centroidY };
}

const SitePlanPage: React.FC = () => {
  const { currentUser } = useContext(AuthContext);
  const { propertyId } = useParams<{ propertyId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { data: sitePlanData } = useGetSitePlan(propertyId);
  const { data: directions } = useGetAllDirections();
  const { data: colors } = useGetAllColors();

  const { mutateAsync: uploadSitePlan, isLoading: isUploading } =
    useUploadSitePlan();
  const { mutateAsync: resetSitePlan, isLoading: isResetting } =
    useResetSitePlan();

  const { data: savedMapState, refetch: refetchSitePlanState } = useGetMapState(
    propertyId || "",
    "siteplan"
  );
  const { mutateAsync: saveMapState } = useCreateMapState();
  const { mutateAsync: clearMapState } = useClearMapState();

  const [openClearDialog, setOpenClearDialog] = useState(false);

  const [sitePlanImage, setSitePlanImage] = useState<string>("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [rotation, setRotation] = useState<number>(0);

  // boundary pins
  const [pins, setPins] = useState<Pin[]>([]);
  const [hasPinnedBoundary, setHasPinnedBoundary] = useState(false);

  console.log(hasPinnedBoundary);

  const [droppedItems, setDroppedItems] = useState<DroppedItem[]>([]);

  const [isPinning, setIsPinning] = useState<boolean>(true);
  const [draggingPinIndex, setDraggingPinIndex] = useState<number | null>(null);
  const [pinDragOffset, setPinDragOffset] = useState({ x: 0, y: 0 });

  const [draggingItemId, setDraggingItemId] = useState<string | null>(null);
  const [resizingItemId, setResizingItemId] = useState<string | null>(null);
  const [rotatingItemId, setRotatingItemId] = useState<string | null>(null);
  const [resizeCorner, setResizeCorner] = useState<Corner | null>(null);

  const [initialItemPos, setInitialItemPos] = useState({ x: 0, y: 0 });
  const [initialMouse, setInitialMouse] = useState({ x: 0, y: 0 });
  const [initialSize, setInitialSize] = useState({ width: 0, height: 0 });
  const rotateStartRef = useRef<{ angle: number }>({ angle: 0 });
  const initialAngleRef = useRef<number>(0);

  const [selectedItemId, setSelectedItemId] = useState<string>("");
  const [colorAnchorEl, setColorAnchorEl] = useState<null | HTMLElement>(null);
  const [colorMenuOpen, setColorMenuOpen] = useState<boolean>(false);

  const [showCompass, setShowCompass] = useState<boolean>(false);
  const [compassOpacity, setCompassOpacity] = useState<number>(1);
  const [compassCenter, setCompassCenter] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [showMarma, setShowMarma] = useState<boolean>(false);
  const [marmaPoints, setMarmaPoints] = useState<any[]>([]);
  const [marmaLines, setMarmaLines] = useState<any[]>([]);
  const [angleMarkers, setAngleMarkers] = useState<{ x: number; y: number }[]>(
    []
  );

  const transformRef = useRef<ReactZoomPanPinchRef | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState<boolean>(!isMobile);
  const toggleMenu = () => setIsOpen((prev) => !prev);

  const [history, setHistory] = useState<Pin[][]>([]);
  const [historyIndex, setHistoryIndex] = useState<number>(-1);

  useEffect(() => {
    if (sitePlanData?.sitePlanImageUrl) {
      setSitePlanImage(sitePlanData.sitePlanImageUrl);
    }
    if (sitePlanData?.rotationDegree !== undefined) {
      setRotation(Number(sitePlanData.rotationDegree));
    }
  }, [sitePlanData]);

  useEffect(() => {
    if (!savedMapState?.mapState) return;
    const { pins: loadedPins, droppedItems: loadedItems } =
      savedMapState.mapState;

    if (Array.isArray(loadedPins) && loadedPins.length >= 3) {
      setPins(loadedPins);
      setHasPinnedBoundary(true);
      setHistory([loadedPins]);
      setHistoryIndex(0);
      setIsPinning(false);
    } else {
      setPins([]);
      setHasPinnedBoundary(false);
      setHistory([]);
      setHistoryIndex(-1);
      setIsPinning(true);
    }

    if (Array.isArray(loadedItems)) {
      setDroppedItems(loadedItems);
    }
  }, [savedMapState]);

  useBeforeunload((event) => {
    if (!propertyId || !currentUser?.tenant?.id) return;

    const requestBody = {
      pins,
      droppedItems,
      context: "siteplan",
    };
    saveMapState({
      requestBody,
      propertyId,
      tenantId: currentUser.tenant.id,
      context: "siteplan",
    });
    event.preventDefault();
    return "Are you sure you want to leave? Unsaved changes may be lost.";
  });

  useEffect(() => {
    setIsOpen(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    if (pins.length > 2) {
      const center = calculateCentroid(pins);
      setCompassCenter(center);

      const intersections = BOUNDARY_ANGLES.map((ang) =>
        getIntersectionPointXY(center, pins, ang)
      ).filter(Boolean) as { x: number; y: number }[];

      const results = intersectionPointsFromAnglePairsXY(
        center,
        pins,
        anglePairs
      );
      const segments: Array<
        [{ x: number; y: number }, { x: number; y: number }]
      > = [];
      for (const [pA, pB] of results) {
        if (pA && pB) segments.push([pA, pB]);
      }
      const intersectionPointsXY: Array<{ x: number; y: number }> = [];
      for (let i = 0; i < segments.length; i++) {
        const [A1, A2] = segments[i];
        for (let j = i + 1; j < segments.length; j++) {
          const [B1, B2] = segments[j];
          const inter = lineSegmentIntersection2D(A1, A2, B1, B2);
          if (inter) intersectionPointsXY.push(inter);
        }
      }
      setMarmaPoints(intersectionPointsXY);
      setMarmaLines(results);
      setAngleMarkers(intersections);
    } else {
      setCompassCenter(null);
      setAngleMarkers([]);
      setMarmaPoints([]);
      setMarmaLines([]);
    }
  }, [pins]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (selectedFile && propertyId) {
      const response = await uploadSitePlan({ propertyId, file: selectedFile });
      if (response?.sitePlanImageUrl) {
        setSitePlanImage(response.sitePlanImageUrl);
        setRotation(0);
      }
    }
  };

  const handleResetSitePlanImage = async () => {
    if (!propertyId) return;
    try {
      await resetSitePlan({ propertyId });
      setSitePlanImage("");
      setSelectedFile(null);
      toast.success("Site plan has been reset.");
    } catch (err) {
      toast.error("Failed to reset site plan.");
    }
  };

  const handleSaveMapState = useCallback(
    (shouldRefetch: boolean = true) => {
      if (!propertyId || !currentUser?.tenant?.id) return;
      const requestBody = {
        pins,
        droppedItems,
        context: "siteplan",
      };
      saveMapState(
        {
          requestBody,
          propertyId,
          tenantId: currentUser.tenant.id,
          context: "siteplan",
        },
        {
          onSuccess: () => {
            toast.success("Site plan state saved successfully!");
            if (shouldRefetch) refetchSitePlanState();
          },
          onError: () => {
            toast.error("Failed to save map state.");
          },
        }
      );
    },
    [
      propertyId,
      currentUser,
      pins,
      droppedItems,
      saveMapState,
      refetchSitePlanState,
    ]
  );

  const handleClear = async () => {
    if (!propertyId) return;
    await clearMapState({ propertyId, context: "siteplan" });
    setPins([]);
    setDroppedItems([]);
    setHistory([]);
    setHistoryIndex(-1);
    setIsPinning(true);
  };

  const pushPinsToHistory = (newPins: Pin[]) => {
    const newHist = history.slice(0, historyIndex + 1);
    newHist.push(newPins);
    setHistory(newHist);
    setHistoryIndex(newHist.length - 1);
    setPins(newPins);
  };
  const handleUndo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setPins(history[newIndex]);
    }
  };
  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setPins(history[newIndex]);
    }
  };

  const handleImageClick = (
    e: React.MouseEvent<HTMLDivElement>,
    transformState: any
  ) => {
    if (!containerRef.current || !isPinning) return;
    const rect = containerRef.current.getBoundingClientRect();
    const { scale } = transformState;

    const screenX = e.clientX - rect.left;
    const screenY = e.clientY - rect.top;

    const localX = screenX * (1 / scale);
    const localY = screenY * (1 / scale);

    if (pins.length > 0) {
      const firstPin = pins[0];
      const distance = Math.hypot(localX - firstPin.x, localY - firstPin.y);
      if (distance < 30) {
        setIsPinning(false);
        return;
      }
    }
    const newPin: Pin = { x: localX, y: localY };
    pushPinsToHistory([...pins, newPin]);
  };

  const handlePinMouseDown = (
    e: React.MouseEvent<HTMLDivElement>,
    pinIndex: number,
    transformState: any
  ) => {
    e.stopPropagation();
    if (!containerRef.current) return;
    if (isPinning) return;

    const rect = containerRef.current.getBoundingClientRect();
    const { scale } = transformState;
    const screenX = e.clientX - rect.left;
    const screenY = e.clientY - rect.top;

    const localX = screenX * (1 / scale);
    const localY = screenY * (1 / scale);

    const pin = pins[pinIndex];
    setDraggingPinIndex(pinIndex);
    setPinDragOffset({ x: localX - pin.x, y: localY - pin.y });
  };

  const handleDrop = (
    e: React.DragEvent<HTMLDivElement>,
    transformState: any
  ) => {
    e.preventDefault();
    const itemId = e.dataTransfer.getData("item_id");
    const itemType = e.dataTransfer.getData("item_type");
    const itemName = e.dataTransfer.getData("item_name");

    if (itemId && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const { scale } = transformState;

      const screenX = e.clientX - rect.left;
      const screenY = e.clientY - rect.top;

      const dropX = screenX * (1 / scale);
      const dropY = screenY * (1 / scale);

      let itemDirections: string[] = [];
      if (compassCenter && directions) {
        itemDirections = getDirectionsForItemXY(
          {
            id: itemId,
            type: itemType as DroppedItem["type"],
            displayName: itemName,
            x: dropX,
            y: dropY,
            color: "",
            width: 50,
            height: 50,
          },
          compassCenter,
          directions,
          rotation
        );
      }

      if (itemType === "Room") {
        setDroppedItems((prev) => [
          ...prev,
          {
            id: itemId,
            type: "Room",
            displayName: itemName,
            x: dropX,
            y: dropY,
            color: null,
            width: 150,
            height: 50,
            rotation: 0,
            directions: itemDirections,
          },
        ]);
      } else if (
        itemType === "kitchenBurnerSlab" ||
        itemType === "toiletSeat" ||
        itemType === "housePillar"
      ) {
        const whiteColor = colors?.find((c: any) => c.systemName === "white");
        setDroppedItems((prev) => [
          ...prev,
          {
            id: itemId,
            type: itemType as
              | "kitchenBurnerSlab"
              | "toiletSeat"
              | "housePillar",
            displayName: itemName,
            x: dropX,
            y: dropY,
            color: whiteColor?.id || null,
            width: 50,
            height: 50,
            rotation: 0,
            directions: itemDirections,
          },
        ]);
        setSelectedItemId(itemId);
      }
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleTouchDrop = (
    itemId: string,
    itemName: string,
    itemType: string,
    clientX: number,
    clientY: number
  ) => {
    if (!containerRef.current || !transformRef.current) return;
    const { scale } = transformRef.current.instance.transformState;
    const rect = containerRef.current.getBoundingClientRect();

    const screenX = clientX - rect.left;
    const screenY = clientY - rect.top;
    const dropX = screenX * (1 / scale);
    const dropY = screenY * (1 / scale);

    let itemDirections: string[] = [];
    if (compassCenter && directions) {
      itemDirections = getDirectionsForItemXY(
        {
          id: itemId,
          type: itemType as DroppedItem["type"],
          displayName: itemName,
          x: dropX,
          y: dropY,
          color: "",
          width: 50,
          height: 50,
        },
        compassCenter,
        directions,
        rotation
      );
    }

    if (itemType === "Room") {
      setDroppedItems((prev) => [
        ...prev,
        {
          id: itemId,
          type: "Room",
          displayName: itemName,
          x: dropX,
          y: dropY,
          color: null,
          width: 150,
          height: 50,
          rotation: 0,
          directions: itemDirections,
        },
      ]);
    } else if (
      itemType === "kitchenBurnerSlab" ||
      itemType === "toiletSeat" ||
      itemType === "housePillar"
    ) {
      const whiteColor = colors?.find((c: any) => c.systemName === "white");
      setDroppedItems((prev) => [
        ...prev,
        {
          id: itemId,
          type: itemType as "kitchenBurnerSlab" | "toiletSeat" | "housePillar",
          displayName: itemName,
          x: dropX,
          y: dropY,
          color: whiteColor?.id || null,
          width: 50,
          height: 50,
          rotation: 0,
          directions: itemDirections,
        },
      ]);
      setSelectedItemId(itemId);
    }
  };

  const handleItemMouseDown = (
    e: React.MouseEvent<HTMLDivElement>,
    itemId: string
  ) => {
    e.preventDefault();
    e.stopPropagation();
    if (!containerRef.current || !transformRef.current) return;

    setDraggingItemId(itemId);
    const rect = containerRef.current.getBoundingClientRect();
    const { scale } = transformRef.current.instance.transformState;

    const screenX = e.clientX - rect.left;
    const screenY = e.clientY - rect.top;
    const localX = screenX * (1 / scale);
    const localY = screenY * (1 / scale);

    const item = droppedItems.find((itm) => itm.id === itemId);
    if (!item) return;
    const itemX = item.x ?? 0;
    const itemY = item.y ?? 0;
    setInitialItemPos({ x: itemX, y: itemY });
    setInitialMouse({ x: localX, y: localY });
  };

  const handleItemTouchStart = (
    e: React.TouchEvent<HTMLDivElement>,
    itemId: string
  ) => {
    if (e.touches.length > 1) return;
    e.preventDefault();
    e.stopPropagation();
    if (!containerRef.current || !transformRef.current) return;

    setDraggingItemId(itemId);
    const touch = e.touches[0];
    const rect = containerRef.current.getBoundingClientRect();
    const { scale } = transformRef.current.instance.transformState;

    const screenX = touch.clientX - rect.left;
    const screenY = touch.clientY - rect.top;
    const localX = screenX * (1 / scale);
    const localY = screenY * (1 / scale);

    const item = droppedItems.find((itm) => itm.id === itemId);
    if (!item) return;
    const itemX = item.x ?? 0;
    const itemY = item.y ?? 0;
    setInitialItemPos({ x: itemX, y: itemY });
    setInitialMouse({ x: localX, y: localY });
  };

  const handleResizeMouseDown = (
    e: React.MouseEvent<HTMLDivElement>,
    itemId: string,
    corner: Corner
  ) => {
    e.preventDefault();
    e.stopPropagation();
    if (!containerRef.current || !transformRef.current) return;
    setResizingItemId(itemId);
    setResizeCorner(corner);

    const rect = containerRef.current.getBoundingClientRect();
    const { scale } = transformRef.current.instance.transformState;

    const screenX = e.clientX - rect.left;
    const screenY = e.clientY - rect.top;
    const localX = screenX * (1 / scale);
    const localY = screenY * (1 / scale);

    const item = droppedItems.find((itm) => itm.id === itemId);
    if (!item) return;
    const itemWidth = item.width ?? 0;
    const itemHeight = item.height ?? 0;
    setInitialSize({ width: itemWidth, height: itemHeight });
    setInitialMouse({ x: localX, y: localY });
  };

  const handleResizeTouchStart = (
    e: React.TouchEvent<HTMLDivElement>,
    itemId: string,
    corner: Corner
  ) => {
    if (e.touches.length > 1) return;
    e.preventDefault();
    e.stopPropagation();
    if (!containerRef.current || !transformRef.current) return;

    setResizingItemId(itemId);
    setResizeCorner(corner);

    const touch = e.touches[0];
    const rect = containerRef.current.getBoundingClientRect();
    const { scale } = transformRef.current.instance.transformState;
    const screenX = touch.clientX - rect.left;
    const screenY = touch.clientY - rect.top;
    const localX = screenX * (1 / scale);
    const localY = screenY * (1 / scale);

    const item = droppedItems.find((itm) => itm.id === itemId);
    if (!item) return;
    const itemWidth = item.width ?? 0;
    const itemHeight = item.height ?? 0;
    setInitialSize({ width: itemWidth, height: itemHeight });
    setInitialMouse({ x: localX, y: localY });
  };

  const handleRotateMouseDown = (
    e: React.MouseEvent<HTMLDivElement>,
    itemId: string
  ) => {
    e.preventDefault();
    e.stopPropagation();
    if (!containerRef.current || !transformRef.current) return;

    setRotatingItemId(itemId);
    const rect = containerRef.current.getBoundingClientRect();
    const { scale } = transformRef.current.instance.transformState;

    const screenX = e.clientX - rect.left;
    const screenY = e.clientY - rect.top;
    const localX = screenX * (1 / scale);
    const localY = screenY * (1 / scale);

    const item = droppedItems.find((itm) => itm.id === itemId);
    if (!item) return;
    const centerX = item.x ?? 0;
    const centerY = item.y ?? 0;

    rotateStartRef.current.angle = Math.atan2(
      localY - centerY,
      localX - centerX
    );
    initialAngleRef.current = item.rotation || 0;
  };

  const handleRotateTouchStart = (
    e: React.TouchEvent<HTMLDivElement>,
    itemId: string
  ) => {
    if (e.touches.length > 1) return;
    e.preventDefault();
    e.stopPropagation();
    if (!containerRef.current || !transformRef.current) return;

    setRotatingItemId(itemId);
    const touch = e.touches[0];
    const rect = containerRef.current.getBoundingClientRect();
    const { scale } = transformRef.current.instance.transformState;

    const screenX = touch.clientX - rect.left;
    const screenY = touch.clientY - rect.top;
    const localX = screenX * (1 / scale);
    const localY = screenY * (1 / scale);

    const item = droppedItems.find((itm) => itm.id === itemId);
    if (!item) return;
    const centerX = item.x ?? 0;
    const centerY = item.y ?? 0;

    rotateStartRef.current.angle = Math.atan2(
      localY - centerY,
      localX - centerX
    );
    initialAngleRef.current = item.rotation || 0;
  };

  const handleCutItem = (itemId: string) => {
    setDroppedItems((prev) => prev.filter((itm) => itm.id !== itemId));
  };

  const handleItemContextMenu = (e: React.MouseEvent, itemId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedItemId(itemId);
    setColorAnchorEl(e.currentTarget as HTMLElement);
    setColorMenuOpen(true);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current || !transformRef.current) return;
      const { scale } = transformRef.current.instance.transformState;
      const rect = containerRef.current.getBoundingClientRect();

      const screenX = e.clientX - rect.left;
      const screenY = e.clientY - rect.top;
      const localX = screenX * (1 / scale);
      const localY = screenY * (1 / scale);

      if (draggingPinIndex !== null && !isPinning) {
        setPins((prev) => {
          const updated = [...prev];
          updated[draggingPinIndex] = {
            ...updated[draggingPinIndex],
            x: localX - pinDragOffset.x,
            y: localY - pinDragOffset.y,
          };
          return updated;
        });
      }

      if (draggingItemId) {
        const dx = localX - initialMouse.x;
        const dy = localY - initialMouse.y;
        setDroppedItems((prev) =>
          prev.map((item) =>
            item.id === draggingItemId
              ? {
                  ...item,
                  x: initialItemPos.x + dx,
                  y: initialItemPos.y + dy,
                }
              : item
          )
        );
      }

      if (resizingItemId && resizeCorner) {
        const dx = localX - initialMouse.x;
        const dy = localY - initialMouse.y;
        setDroppedItems((prev) =>
          prev.map((item) => {
            if (item.id !== resizingItemId) return item;

            let newWidth = item.width;
            let newHeight = item.height;

            newWidth = Math.max(20, initialSize.width + dx);
            newHeight = Math.max(20, initialSize.height + dy);

            return {
              ...item,
              width: newWidth,
              height: newHeight,
            };
          })
        );
      }

      if (rotatingItemId) {
        const item = droppedItems.find((i) => i.id === rotatingItemId);
        if (!item) return;
        const centerX = item.x ?? 0;
        const centerY = item.y ?? 0;
        const currentAngle = Math.atan2(localY - centerY, localX - centerX);
        const deltaAngleDeg =
          (currentAngle - rotateStartRef.current.angle) * (180 / Math.PI);
        const newAngle = initialAngleRef.current + deltaAngleDeg;
        setDroppedItems((prev) =>
          prev.map((i) =>
            i.id === rotatingItemId ? { ...i, rotation: newAngle } : i
          )
        );
      }
    };

    const handleMouseUp = () => {
      if (draggingPinIndex !== null) {
        pushPinsToHistory([...pins]);
        setDraggingPinIndex(null);
      }
      // item drag: recalc direction
      if (draggingItemId) {
        if (compassCenter && directions) {
          setDroppedItems((prev) =>
            prev.map((item) => {
              if (item.id === draggingItemId) {
                const newDirections = getDirectionsForItemXY(
                  item,
                  compassCenter,
                  directions,
                  rotation
                );
                return { ...item, directions: newDirections };
              }
              return item;
            })
          );
        }
        setDraggingItemId(null);
      }
      // item resize: recalc direction
      if (resizingItemId) {
        const rId = resizingItemId;
        setResizingItemId(null);
        setResizeCorner(null);

        if (compassCenter && directions) {
          const item = droppedItems.find((x) => x.id === rId);
          if (item) {
            const newDirections = getDirectionsForItemXY(
              item,
              compassCenter,
              directions,
              rotation
            );
            setDroppedItems((prev) =>
              prev.map((i) =>
                i.id === rId ? { ...i, directions: newDirections } : i
              )
            );
          }
        }
      }
      // item rotate: recalc direction
      if (rotatingItemId) {
        const rId = rotatingItemId;
        setRotatingItemId(null);
        if (compassCenter && directions) {
          const item = droppedItems.find((x) => x.id === rId);
          if (item) {
            const newDirections = getDirectionsForItemXY(
              item,
              compassCenter,
              directions,
              rotation
            );
            setDroppedItems((prev) =>
              prev.map((i) =>
                i.id === rId ? { ...i, directions: newDirections } : i
              )
            );
          }
        }
      }
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [
    draggingPinIndex,
    pinDragOffset,
    isPinning,
    pins,
    draggingItemId,
    resizingItemId,
    rotatingItemId,
    resizeCorner,
    initialItemPos,
    initialMouse,
    initialSize,
    directions,
    droppedItems,
    compassCenter,
    rotation,
    pushPinsToHistory,
  ]);

  useEffect(() => {
    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 1) return;
      if (!containerRef.current || !transformRef.current) return;
      e.preventDefault();

      const touch = e.touches[0];
      const { scale } = transformRef.current.instance.transformState;
      const rect = containerRef.current.getBoundingClientRect();

      const screenX = touch.clientX - rect.left;
      const screenY = touch.clientY - rect.top;
      const localX = screenX * (1 / scale);
      const localY = screenY * (1 / scale);

      if (draggingPinIndex !== null && !isPinning) {
        setPins((prev) => {
          const updated = [...prev];
          updated[draggingPinIndex] = {
            ...updated[draggingPinIndex],
            x: localX - pinDragOffset.x,
            y: localY - pinDragOffset.y,
          };
          return updated;
        });
      }

      if (draggingItemId) {
        const dx = localX - initialMouse.x;
        const dy = localY - initialMouse.y;
        setDroppedItems((prev) =>
          prev.map((item) =>
            item.id === draggingItemId
              ? {
                  ...item,
                  x: initialItemPos.x + dx,
                  y: initialItemPos.y + dy,
                }
              : item
          )
        );
      }

      if (resizingItemId && resizeCorner) {
        const dx = localX - initialMouse.x;
        const dy = localY - initialMouse.y;
        setDroppedItems((prev) =>
          prev.map((item) => {
            if (item.id !== resizingItemId) return item;
            let newWidth = Math.max(20, initialSize.width + dx);
            let newHeight = Math.max(20, initialSize.height + dy);
            return {
              ...item,
              width: newWidth,
              height: newHeight,
            };
          })
        );
      }

      if (rotatingItemId) {
        const item = droppedItems.find((i) => i.id === rotatingItemId);
        if (!item) return;
        const centerX = item.x ?? 0;
        const centerY = item.y ?? 0;
        const currentAngle = Math.atan2(localY - centerY, localX - centerX);
        const deltaAngleDeg =
          (currentAngle - rotateStartRef.current.angle) * (180 / Math.PI);
        const newAngle = initialAngleRef.current + deltaAngleDeg;
        setDroppedItems((prev) =>
          prev.map((i) =>
            i.id === rotatingItemId ? { ...i, rotation: newAngle } : i
          )
        );
      }
    };

    const handleTouchEndOrCancel = () => {
      if (draggingPinIndex !== null) {
        pushPinsToHistory([...pins]);
        setDraggingPinIndex(null);
      }
      if (draggingItemId) {
        if (compassCenter && directions) {
          const dId = draggingItemId;
          setDroppedItems((prev) =>
            prev.map((item) => {
              if (item.id === dId) {
                const newDirections = getDirectionsForItemXY(
                  item,
                  compassCenter,
                  directions,
                  rotation
                );
                return { ...item, directions: newDirections };
              }
              return item;
            })
          );
        }
        setDraggingItemId(null);
      }
      if (resizingItemId) {
        const rId = resizingItemId;
        setResizingItemId(null);
        setResizeCorner(null);
        if (compassCenter && directions) {
          const item = droppedItems.find((x) => x.id === rId);
          if (item) {
            const newDirections = getDirectionsForItemXY(
              item,
              compassCenter,
              directions,
              rotation
            );
            setDroppedItems((prev) =>
              prev.map((i) =>
                i.id === rId ? { ...i, directions: newDirections } : i
              )
            );
          }
        }
      }
      if (rotatingItemId) {
        const rId = rotatingItemId;
        setRotatingItemId(null);
        if (compassCenter && directions) {
          const item = droppedItems.find((x) => x.id === rId);
          if (item) {
            const newDirections = getDirectionsForItemXY(
              item,
              compassCenter,
              directions,
              rotation
            );
            setDroppedItems((prev) =>
              prev.map((i) =>
                i.id === rId ? { ...i, directions: newDirections } : i
              )
            );
          }
        }
      }
    };

    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", handleTouchEndOrCancel);
    document.addEventListener("touchcancel", handleTouchEndOrCancel);

    return () => {
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEndOrCancel);
      document.removeEventListener("touchcancel", handleTouchEndOrCancel);
    };
  }, [
    draggingPinIndex,
    isPinning,
    pinDragOffset,
    draggingItemId,
    resizingItemId,
    rotatingItemId,
    resizeCorner,
    initialItemPos,
    initialMouse,
    initialSize,
    directions,
    droppedItems,
    compassCenter,
    rotation,
    pins,
    pushPinsToHistory,
  ]);

  const handleColorSelect = (colorId: string) => {
    setDroppedItems((prev) =>
      prev.map((item) =>
        item.id === selectedItemId ? { ...item, color: colorId } : item
      )
    );
    setColorMenuOpen(false);
  };

  const toggleShowCompass = () => setShowCompass((prev) => !prev);
  const toggleShowMarma = () => setShowMarma((prev) => !prev);

  if (!sitePlanImage) {
    return (
      <Box
        sx={{
          position: "relative",
          width: "100%",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pt: { xs: 4, sm: 6 },
          pb: { xs: 2, sm: 4 },
        }}
      >
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            position: "absolute",
            top: { xs: 16, sm: 24 },
            left: { xs: 16, sm: 24 },
            backgroundColor: "#bb0000",
            color: "white",
            zIndex: 2000,
            "&:hover": {
              backgroundColor: "#990000",
            },
            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
          }}
        >
          <ArrowBackIcon />
        </IconButton>

        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          height="70vh"
          textAlign="center"
          sx={{ backgroundColor: "#f5f7f9", borderRadius: 2, width: "90%" }}
        >
          <Box mb={4}>
            <UploadFileIcon
              sx={{ fontSize: 64, color: "text.secondary", opacity: 0.7 }}
            />
          </Box>

          <Typography
            variant="h5"
            fontWeight="500"
            color="textSecondary"
            gutterBottom
          >
            No Site Plan Found
          </Typography>

          <Typography
            variant="body1"
            color="textSecondary"
            paragraph
            sx={{ maxWidth: "450px", mb: 4 }}
          >
            Upload a site plan image to begin your analysis. Supported formats:
            JPG and PNG.
          </Typography>

          <Box
            sx={{
              padding: 4,
              borderRadius: 2,
              width: "100%",
              maxWidth: "450px",
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              backgroundColor: "#fff",
              boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
            }}
          >
            <Box
              sx={{
                border: "2px dashed #e0e0e0",
                borderRadius: 1,
                py: 4,
                px: 2,
                width: "100%",
                mb: 3,
                backgroundColor: "#fafafa",
              }}
            >
              <input
                type="file"
                accept="image/*,application/pdf"
                onChange={handleFileChange}
                style={{ display: "none" }}
                id="site-plan-upload"
              />
              <label htmlFor="site-plan-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<AddIcon />}
                  sx={{ mb: 2 }}
                >
                  Select File
                </Button>
              </label>

              {selectedFile ? (
                <Box
                  mt={2}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <InsertDriveFileIcon sx={{ mr: 1 }} />
                  <Typography variant="body2" fontWeight="500">
                    {selectedFile.name}
                  </Typography>
                </Box>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Drag and drop a file or click to browse
                </Typography>
              )}
            </Box>

            <Button
              variant="contained"
              color="primary"
              onClick={handleUpload}
              disabled={!selectedFile || isUploading}
              fullWidth
              sx={{
                py: 1.5,
                fontSize: "0.95rem",
                textTransform: "none",
                fontWeight: 500,
                borderRadius: 1,
              }}
              startIcon={
                isUploading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <CloudUploadIcon />
                )
              }
            >
              {isUploading ? "Uploading..." : "Upload Site Plan"}
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100vh",
        overflow: "hidden",
      }}
    >
      <IconButton
        onClick={() => navigate(-1)}
        sx={{
          position: "absolute",
          top: { xs: 16, sm: 24 },
          left: { xs: 16, sm: 24 },
          backgroundColor: "#bb0000",
          color: "white",
          zIndex: 2000,
          "&:hover": { backgroundColor: "#990000" },
          boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
        }}
      >
        <ArrowBackIcon />
      </IconButton>

      <Box
        sx={{
          position: "absolute",
          bottom: { xs: 16, sm: 24 },
          left: { xs: 16, sm: 24 },
          zIndex: 1500,
        }}
      >
        <Button
          variant="outlined"
          color="error"
          onClick={handleResetSitePlanImage}
          disabled={isResetting}
        >
          {isResetting ? "Resetting..." : "Reset Site Plan"}
        </Button>
      </Box>

      <TransformWrapper
        ref={transformRef}
        initialScale={1}
        minScale={0.2}
        maxScale={10}
        limitToBounds={false}
        doubleClick={{ disabled: true }}
        panning={{
          disabled:
            isPinning ||
            draggingPinIndex !== null ||
            draggingItemId !== null ||
            resizingItemId !== null ||
            rotatingItemId !== null,
        }}
      >
        {({ zoomIn, zoomOut, resetTransform, instance }) => (
          <>
            <TransformComponent
              wrapperStyle={{ width: "100%", height: "auto" }}
              contentStyle={{ width: "100%", height: "100%" }}
            >
              <div
                ref={containerRef}
                style={{ position: "relative", width: "100%", height: "100%" }}
                onClick={(e) => handleImageClick(e, instance.transformState)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, instance.transformState)}
              >
                <img
                  src={sitePlanImage}
                  alt="Site Plan"
                  style={{
                    display: "block",
                    maxWidth: "100%",
                    height: "auto",
                  }}
                />

                {pins.length > 1 && (
                  <svg
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      pointerEvents: "none",
                    }}
                  >
                    <polygon
                      points={pins.map((p) => `${p.x},${p.y}`).join(" ")}
                      fill="none"
                      stroke="#bb0000"
                      strokeWidth={4}
                    />
                  </svg>
                )}

                {pins.map((pin, i) => {
                  const isDragging = draggingPinIndex === i;
                  return (
                    <Box
                      key={i}
                      sx={{
                        position: "absolute",
                        left: pin.x,
                        top: pin.y,
                        transform: "translate(-50%, -50%)",
                        width: isPinning ? 24 : 14,
                        height: isPinning ? 24 : 14,
                        cursor: isPinning ? "pointer" : "grab",
                        transition: "transform 0.2s ease",
                        backgroundImage: isPinning ? `url(${pinSvg})` : "none",
                        backgroundSize: "contain",
                        backgroundRepeat: "no-repeat",
                        borderRadius: isPinning ? 0 : "50%",
                        backgroundColor: isPinning ? "unset" : "#bb0000",
                        border: isPinning ? "none" : "2px solid #fff",
                        "&:hover": {
                          backgroundColor: isPinning
                            ? "transparent"
                            : "#1ec31e",
                          transform: isPinning
                            ? "translate(-50%, -50%) scale(1.05)"
                            : "translate(-50%, -50%) scale(1.1)",
                        },
                        boxShadow: isPinning
                          ? "none"
                          : isDragging
                          ? "0 0 10px rgba(0,0,0,0.4)"
                          : "0 2px 4px rgba(0,0,0,0.2)",
                      }}
                      onMouseDown={(e) =>
                        handlePinMouseDown(e, i, instance.transformState)
                      }
                      onTouchStart={(e) => {
                        if (e.touches.length > 1) return;
                        handlePinMouseDown(
                          {
                            ...e,
                            clientX: e.touches[0].clientX,
                            clientY: e.touches[0].clientY,
                            preventDefault: () => {},
                            stopPropagation: () => {},
                          } as unknown as React.MouseEvent<HTMLDivElement>,
                          i,
                          instance.transformState
                        );
                      }}
                    />
                  );
                })}

                <SitePlanDroppedItemsOverlay
                  droppedItems={droppedItems}
                  selectedItemId={selectedItemId}
                  onSelectItem={(id: string) => setSelectedItemId(id)}
                  onMouseDown={(
                    e: React.MouseEvent<HTMLDivElement>,
                    itemId: string
                  ) => handleItemMouseDown(e, itemId)}
                  onTouchStart={(
                    e: React.TouchEvent<HTMLDivElement>,
                    itemId: string
                  ) => handleItemTouchStart(e, itemId)}
                  onContextMenu={(e, itemId) =>
                    handleItemContextMenu(e, itemId)
                  }
                  onResizeMouseDown={(
                    e: React.MouseEvent<HTMLDivElement>,
                    itemId: string,
                    corner: Corner
                  ) => handleResizeMouseDown(e, itemId, corner)}
                  onResizeTouchStart={(
                    e: React.TouchEvent<HTMLDivElement>,
                    itemId: string,
                    corner: Corner
                  ) => handleResizeTouchStart(e, itemId, corner)}
                  onRotateMouseDown={(
                    e: React.MouseEvent<HTMLDivElement>,
                    itemId: string
                  ) => handleRotateMouseDown(e, itemId)}
                  onRotateTouchStart={(
                    e: React.TouchEvent<HTMLDivElement>,
                    itemId: string
                  ) => handleRotateTouchStart(e, itemId)}
                  onCutItem={handleCutItem}
                  colors={colors}
                />

                {showCompass && compassCenter && (
                  <Box
                    sx={{
                      position: "absolute",
                      left: `${compassCenter.x}px`,
                      top: `${compassCenter.y}px`,
                      transform: "translate(-50%, -50%)",
                      opacity: compassOpacity,
                      pointerEvents: "none",
                      filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
                      transition: "opacity 0.3s ease",
                    }}
                  >
                    <Compass rotation={rotation} />
                  </Box>
                )}

                {showMarma &&
                  marmaPoints.map((pt, idx) => (
                    <div
                      key={idx}
                      style={{
                        position: "absolute",
                        left: pt.x - 5,
                        top: pt.y - 5,
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        backgroundColor: "red",
                        pointerEvents: "none",
                      }}
                    />
                  ))}
                {showMarma &&
                  angleMarkers.map((pt, idx) => (
                    <div
                      key={idx}
                      style={{
                        position: "absolute",
                        left: pt.x - 5,
                        top: pt.y - 5,
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        backgroundColor: "blue",
                        pointerEvents: "none",
                      }}
                    />
                  ))}
                {showMarma && (
                  <svg
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      pointerEvents: "none",
                    }}
                  >
                    {marmaLines.map((line, idx) => {
                      const [p1, p2] = line;
                      if (!p1 || !p2) return null;
                      return (
                        <line
                          key={idx}
                          x1={p1.x}
                          y1={p1.y}
                          x2={p2.x}
                          y2={p2.y}
                          stroke="rgba(0, 100, 255, 0.5)"
                          strokeWidth={2}
                          strokeDasharray="5,5"
                        />
                      );
                    })}
                  </svg>
                )}
              </div>
            </TransformComponent>

            <Box
              sx={{
                position: "absolute",
                bottom: { xs: 16, sm: 24 },
                right: { xs: 16, sm: 24 },
                display: "flex",
                flexDirection: "column",
                gap: 1,
                zIndex: 1000,
              }}
            >
              <IconButton
                onClick={() => zoomIn()}
                sx={{
                  backgroundColor: "white",
                  color: "#bb0000",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                }}
              >
                +
              </IconButton>
              <IconButton
                onClick={() => zoomOut()}
                sx={{
                  backgroundColor: "white",
                  color: "#bb0000",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                }}
              >
                -
              </IconButton>
              <IconButton
                onClick={() => resetTransform()}
                sx={{
                  backgroundColor: "white",
                  color: "#bb0000",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                  boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                }}
              >
                R
              </IconButton>
            </Box>
          </>
        )}
      </TransformWrapper>

      {!isOpen && (
        <IconButton
          onClick={toggleMenu}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            backgroundColor: "white",
            color: "#bb0000",
            zIndex: 2000,
            "&:hover": {
              backgroundColor: "#f5f5f5",
            },
            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      <ActivityBar
        isOpen={isOpen}
        toggleMenu={toggleMenu}
        onCompassOpacityChange={setCompassOpacity}
        showCompass={showCompass}
        toggleShowCompass={toggleShowCompass}
        showMarma={showMarma}
        toggleShowMarma={toggleShowMarma}
        handleUndo={handleUndo}
        handleRedo={handleRedo}
        canUndo={historyIndex > 0}
        canRedo={historyIndex < history.length - 1}
        isPinning={isPinning}
        droppedItems={droppedItems}
        directions={directions}
        sitePlanImage={sitePlanImage}
        setSitePlanImage={() => {}}
        sitePlanPoints={[]}
        setSitePlanPoints={() => {}}
        mapPoints={[]}
        setMapPoints={() => {}}
        isMatchingSitePlanPoints={false}
        setIsMatchingSitePlanPoints={() => {}}
        marmaPoints={marmaPoints}
        confirmClearMap={() => setOpenClearDialog(true)}
        handleSaveMapState={handleSaveMapState}
        isMobile={isMobile}
        onItemDrop={handleTouchDrop}
      />

      <ColorMenu
        anchorEl={colorAnchorEl}
        open={colorMenuOpen}
        onClose={() => setColorMenuOpen(false)}
        colors={colors ?? []}
        onColorSelect={handleColorSelect}
        getColorHex={(cId) => getColorHexCode(cId, colors ?? [])}
      />

      <ConfirmDialog
        open={openClearDialog}
        title="Clear Map"
        description="Are you sure you want to clear all pins and dropped items? This action cannot be undone."
        onConfirm={() => {
          handleClear();
          setOpenClearDialog(false);
        }}
        onCancel={() => setOpenClearDialog(false)}
      />
    </Box>
  );
};

export default SitePlanPage;
